import React, { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import Button from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { useToast } from "../../contexts/ToastContext";
import { adminAPI } from "../../services/api/admin";

export default function AdminSubscriptionPanel({ billingAccountId, onChange }) {
  const { showToast } = useToast();
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [trialDays, setTrialDays] = useState(7);
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);

  const loadPlans = async () => {
    try { setPlans(await adminAPI.getScopedPlans(billingAccountId)); }
    catch (e) { setPlans(await adminAPI.getPlans()); }
  };

  const loadInvoices = async () => {
    setInvLoading(true);
    try { setInvoices(await adminAPI.getInvoices(billingAccountId)); }
    catch (e) { setInvoices([]); }
    finally { setInvLoading(false); }
  };

  useEffect(() => { loadPlans(); loadInvoices(); }, [billingAccountId]);

  const assign = async () => {
    if (!selectedPlanId) return showToast("Pick a plan first", "info");
    try {
      await adminAPI.assignPlan(billingAccountId, selectedPlanId);
      showToast("Plan assigned", "success");
      onChange?.();
    } catch (e) { showToast(e.response?.data?.error || "Assign failed", "error"); }
  };

  const change = async () => {
    if (!selectedPlanId) return showToast("Pick a plan first", "info");
    try {
      await adminAPI.changePlan(billingAccountId, selectedPlanId);
      showToast("Plan changed", "success");
      onChange?.();
    } catch (e) { showToast(e.response?.data?.error || "Change failed", "error"); }
  };

  const extend = async () => {
    try {
      await adminAPI.extendTrial(billingAccountId, trialDays);
      showToast(`Trial extended by ${trialDays} days`, "success");
      onChange?.();
    } catch (e) { showToast("Extend failed", "error"); }
  };

  const renew = async () => {
    try {
      await adminAPI.forceRenew(billingAccountId);
      showToast("Renewal enqueued", "success");
      onChange?.();
    } catch (e) { showToast(e.response?.data?.error || "Renew failed", "error"); }
  };

  const cancel = async () => {
    try {
      await adminAPI.cancelBillingSubscription(billingAccountId, true);
      showToast("Canceled at period end", "success");
      onChange?.();
    } catch (e) { showToast("Cancel failed", "error"); }
  };

  const syncStripe = async () => {
    try {
      const result = await adminAPI.syncFromStripe(billingAccountId);
      const changes = result.changes || [];
      if (changes.length === 0) {
        showToast("Already in sync with Stripe — nothing changed", "success");
      } else {
        showToast(`Synced from Stripe. ${changes.length} change(s): ${changes.join("; ")}`, "success");
      }
      onChange?.();
      loadInvoices();
    } catch (e) {
      showToast(e.response?.data?.error || "Sync failed", "error");
    }
  };

  const markPaid = async (subId) => {
    try {
      await adminAPI.markInvoicePaid(subId);
      showToast("Marked paid + renewal enqueued", "success");
      loadInvoices();
      onChange?.();
    } catch (e) { showToast("Mark paid failed", "error"); }
  };

  const invoiceColumns = [
    { title: "Number", dataIndex: "number", render: v => v || "—" },
    { title: "Status", dataIndex: "status",
      render: v => <Badge variant={v === "paid" ? "success" : v === "open" ? "warning" : "muted"}>{v}</Badge> },
    { title: "Amount",
      render: (_, r) => `$${(r.amount_due / 100).toFixed(2)}` },
    { title: "Created", dataIndex: "created",
      render: v => v ? new Date(v * 1000).toLocaleDateString() : "—" },
    { title: "",
      render: (_, r) => (
        <div className="flex items-center gap-2">
          {r.hosted_invoice_url && <a className="text-primary" href={r.hosted_invoice_url} target="_blank" rel="noreferrer">Open</a>}
          {r.invoice_pdf && <a className="text-primary" href={r.invoice_pdf} target="_blank" rel="noreferrer">PDF</a>}
        </div>
      ) },
  ];

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="text-sm font-semibold text-foreground mb-3">Subscription</div>
        <div className="flex flex-col gap-3 w-full">
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              style={{ minWidth: 280 }}
              value={selectedPlanId ?? ""}
              onChange={(e) => setSelectedPlanId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="" disabled>Pick a plan</option>
              {plans.map(p => (
                <option key={p.id} value={p.id}>
                  {`${p.code} · ${p.monthly_price_cents != null ? `$${(p.monthly_price_cents / 100).toFixed(0)}/mo` : "custom"} · ${p.monthly_credits} credits${p.is_public ? "" : " · PRIVATE"}`}
                </option>
              ))}
            </select>
            <Button variant="secondary" onClick={assign}>Assign</Button>
            <Button variant="secondary" onClick={change}>Change</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground">Extend trial</span>
            <input
              type="number"
              className="w-24 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value === "" ? null : Number(e.target.value))}
              min={1}
              max={180}
            />
            <span className="text-sm text-foreground">days</span>
            <Button variant="secondary" onClick={extend}>Extend</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={renew}>Force renew (grant credits now)</Button>
            <Button variant="secondary" onClick={syncStripe}>↻ Sync from Stripe</Button>
            <Button
              variant="danger"
              onClick={() => { if (window.confirm("Cancel at period end?")) cancel(); }}
            >
              Cancel subscription
            </Button>
          </div>
        </div>
      </Card>
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-foreground">Invoices</div>
          <Button variant="secondary" onClick={loadInvoices}>Refresh</Button>
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                {invoiceColumns.map((col, i) => (
                  <TableHead key={i}>{col.title}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {invLoading ? (
                <TableRow>
                  <TableCell colSpan={invoiceColumns.length} className="text-center text-muted-foreground">Loading…</TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={invoiceColumns.length} className="text-center text-muted-foreground">No data</TableCell>
                </TableRow>
              ) : (
                invoices.map((record) => (
                  <TableRow key={record.id}>
                    {invoiceColumns.map((col, i) => (
                      <TableCell key={i}>
                        {col.render
                          ? col.render(col.dataIndex ? record[col.dataIndex] : undefined, record)
                          : (col.dataIndex ? record[col.dataIndex] : null)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          <em>To trigger credit grant for an unpaid invoice (e.g. wire received out-of-band), use Force renew above.</em>
        </div>
      </Card>
    </div>
  );
}
